import { Component, OnInit, effect, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Meta, Title } from '@angular/platform-browser';
import { Router, NavigationEnd, RouterOutlet } from '@angular/router';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ContentfulService } from './services/contentful.service';
import { EnvironmentVariablesService } from './services/environment-variables.service';
import { TimeoutService } from './services/timeout.service';
import { UserService } from './services/user.service';
import Highcharts from 'highcharts';
import * as HighchartsMap from 'highcharts/highmaps';
import { TourService, TourStepTemplateComponent } from 'ngx-ui-tour-md-menu';
import { NgTourService } from './services/ng-tour-ui.service';
import { NgTourStepsService } from './services/ng-tour-steps.service';

import { GcmLoadingComponent } from '@gcm/gcm-ui-angular';
import { SessionComponent } from './session/session.component';
import { InvestorsStorageService } from './services/investors-storage.service';
import { InvestorMeta } from './models/classes/investor-meta.class';
import { IconSetResponse } from './models/interfaces/contentful-response.interface';
declare function gtag(command: 'config' | 'event', targetIdOrEventName: string, params?: Record<string, unknown>): void;

@Component({
    selector: 'app-root',
    imports: [RouterOutlet, TourStepTemplateComponent, GcmLoadingComponent, SessionComponent],
    templateUrl: './app.component.html'
})
export class AppComponent implements OnInit {
  router = inject(Router);
  private evService = inject(EnvironmentVariablesService);
  private titleService = inject(Title);
  private metaService = inject(Meta);
  private cfService = inject(ContentfulService);
  private userService = inject(UserService);
  timeoutService = inject(TimeoutService);
  private tourService = inject(TourService);
  private ngTourUi = inject(NgTourService);
  private ngTourSteps = inject(NgTourStepsService);
  private investorStorage = inject(InvestorsStorageService);

  containerId = 'clientScopeApp';
  title = 'client-experience';
  private isInitializingInvestors = false;
  private readonly investorsMeta = toSignal(this.investorStorage.getInvestors(), { initialValue: null as unknown as InvestorMeta[] });
  private readonly iconSet = toSignal(this.cfService.getIconSetValue(), { initialValue: null as unknown as Record<string, string> });

  private readonly ensureInvestorsLoaded = effect(() => {
    if (this.userService.authenticated()) {
      // fetch investor metadata so we can use it everywhere and fetch only if the data is not available
      const investorsMeta = this.investorsMeta();
      if ((!investorsMeta || investorsMeta.length === 0) && !this.isInitializingInvestors) {
        this.isInitializingInvestors = true;
        this.investorStorage.initializeInvestorStorage();
      }
    }
  });

  private readonly ensureIconSetLoaded = effect(() => {
    const iconSet = this.iconSet();
    if (!iconSet) {
      // fetch iconSet only if the data is not available
      (this.cfService.fetchCommonAssets('iconUrl', '') as Observable<IconSetResponse>).subscribe((data) => {
        this.cfService.setIconSetValue(data);
      });
    }
  });

  constructor() {
    this.addGAScript();
    this.titleService.setTitle('ClientScope: A GCM Grosvenor Client Portal');
    this.metaService.updateTag({ name: 'description', content: 'ClientScope enables GCM Grosvenor clients to perform portfolio analysis and gain a detailed understanding of their long-term investments.' });
    
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      const urlTree = this.router.parseUrl(event.url);
      if (Object.prototype.hasOwnProperty.call(urlTree.queryParams, 'fromLogin')) {
        // Remove the "fromLogin" parameter.
        delete urlTree.queryParams['fromLogin'];
        // Navigate to the same route with the cleaned-up query parameters.
        this.router.navigateByUrl(urlTree, { replaceUrl: true });
      }
      gtag('config', this.evService.googleAnalyticsID,
        {
          page_path: event.urlAfterRedirects
        }
      );
    });
    this.setHighchartOptions();
    this.timeoutService.setIdleConfigs();
    this.initIfAuthenticated();
  }

  ngOnInit() {
    this.windowsEventListener();
    // Initialize UI Tour so that we can use it everywhere wanted.
    this.initializeUiTour();
  }

  setHighchartOptions() {
    Highcharts.setOptions({
      chart: {
        style: {
          fontFamily: 'Open Sans'
        }
      },
      credits: {
        enabled: false
      }
    });
    HighchartsMap.setOptions({
      chart: {
        style: {
          fontFamily: 'Open Sans'
        }
      },
      credits: {
        enabled: false
      }
    });
  }

  /** Add Google Analytics Script Dynamically */
  addGAScript() {
    const gtagScript: HTMLScriptElement = document.createElement('script');
    gtagScript.async = true;
    gtagScript.src = 'https://www.googletagmanager.com/gtag/js?id=' + this.evService.googleAnalyticsID;
    document.head.prepend(gtagScript);
    /** Disable automatic page view hit to fix duplicate page view count  **/
    gtag('config', this.evService.googleAnalyticsID, { send_page_view: false });
  }
  
  initIfAuthenticated() {
    effect(() => {
      if(this.userService.authenticated()){
          this.cfService.initializeContentfulKey();
        }
    });
  }

  windowsEventListener() {
    window.addEventListener('storage', (event) => {
      if (event.key === 'session-continue') {
        this.timeoutService.resetSessionIdleData();
      }
    }, false);
  }

  initializeUiTour() {
    this.ngTourUi.getTourSteps().subscribe(({ steps, stepDefaults }) => {
      this.tourService.initialize(steps, stepDefaults ?? this.ngTourSteps.defaultOptions);
    });
  }
}
